<template>
  <div>
    <el-card v-loading="isLoading" shadow="always">
      <template #header>
        <el-row type="flex" justify="space-between" align="center">
          <el-col :md="8" :sm="8" :xs="10"><el-row type="flex" justify="start">
              <h4>{{ $route.name }}</h4>
            </el-row></el-col>
        </el-row>
      </template>
      <el-row type="flex" align="center" :gutter="4">
        <base-select v-model="selectedMonth" class="flex-max flex-1-1 my-auto pb-1" size="small"
          placeholder="Selecione o mês">
          <el-option v-for="month in LastTwoYears" :key="month.name" :label="month.name"
            :value="month.date"></el-option>
        </base-select>
        <base-button onlyIcon type="secondary" icon="el-icon-download" flex="flex-fit" text="Baixar relatório"
          @click="downloadPDF" title="Baixar relatório" />
      </el-row>
      <el-row type="flex" :gutter="4">
        <base-select :modelValue="sortState.prop" @change="handleSortChange({prop: $event, order: sortState.order})">
        <el-option v-for="sortColumn in sortColumns" :label="sortColumn.label" :value="sortColumn.value"
          :key="sortColumn">
        </el-option>
      </base-select>
      <base-select :modelValue="sortState.order" @change="handleSortChange({prop: sortState.prop, order: $event})">
        <el-option v-for="sortColumn in sortColumnsOrder" :label="sortColumn.label" :value="sortColumn.value"
          :key="sortColumn">
        </el-option>
      </base-select>
      </el-row>
      <el-divider />
      <div v-if="isLoaded">
        <div v-for="[categoryName, sells] in SortedData" :key="getIds(sells)">
          <h4 class="text-start">{{ categoryName }}</h4>
          <el-table :data="sells">
            <el-table-column label="PRODUTO" prop="productName" />
            <el-table-column v-for="shop in Shops" :key="shop.uid" :label="shop.name" :formatter="(row) =>
                `${numberFormatter.format(
                  Number(row[shop.firm_code]?.quantity || 0)
                )} kg - ${currencyFormatter.format(
                  Number(row[shop.firm_code]?.total || 0)
                )} (${currencyFormatter.format(
                  Number(row[shop.firm_code]?.total || 0) /
                  Number(row[shop.firm_code]?.quantity || 0)
                )})`
              " sortable="custom" />

            <el-table-column label="TOTAL VENDIDO">
              <template #default="{ row: sell }">
                <strong>
                  {{
                    numberFormatter.format(
                      Object.values(sell).reduce(
                        (sum, { quantity }) =>
                          quantity ? sum + Number(quantity) : sum,
                        0
                      )
                    )
                  }}
                  kg (
                  {{
                    currencyFormatter.format(
                      Object.values(sell).reduce(
                        (sum, { total }) => (total ? sum + Number(total) : sum),
                        0
                      )
                    )
                  }}
                  )
                </strong>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <el-empty v-else description="Selecione um açougue pra iniciar"></el-empty>
    </el-card>
  </div>
</template>

<script>
import FirmService from "../services/firms";
import SellService from "../services/sells";
import BaseButton from "../components/BaseButton.vue";
import BaseSelect from "../components/BaseSelect.vue";
import { generatePDF } from "../services/reports";
import { uuid } from "vue-uuid";
import {
  dateFormatter,
  currencyFormatter,
  numberFormatter,
} from "../utils/formatters";

const sortColumns = [
  {
    label: "Produto",
    value: "productName"
  }, {
    label: "Valor",
    value: "total"
  }
]

const sortColumnsOrder = [
  {
    label: "Ascendente",
    value: "ascending"
  }, {
    label: "Descendente",
    value: "descending"
  }
]

const PRODUCT_CATEGORIES = {
  25: {
    11: "PRODUTOS PARA CHURRASCO",
  },
  20: {
    5: "BEBIDAS",
  },
  17: {
    10: "PRODUTOS PARA CHURRASCO",
  },
  14: {
    7: "REVENDA",
  },
  16: {
    9: "MERCEARIA",
  },
  1: {
    1: "LENHA",
    13: "FARINÁCEOS",
  },
};

export default {
  name: "ShopSellsTrends",
  components: {
    BaseButton,
    BaseSelect,
  },
  data: () => ({
    showFilterOrdersModal: false,
    orders: null,
    chartKey: uuid.v4(),
    selectedMonth: null,
    filterOptions: {
      status: ["completed", "paid", "finalized"],
      delivered_at_start: getFirstDayInMonth(),
      delivered_at_end: new Date(),
    },
    isLoadingDownloadButton: false,
    isLoading: false,
    sortColumns,
    sortColumnsOrder,
    dateFormatter,
    currencyFormatter,
    numberFormatter,
    isLoaded: false,
    dateMonthFormatter: new Intl.DateTimeFormat("pt-BR", {
      month: "long",
    }),
    selectedShop: null,
    firms: null,
    sells: null,
    sumBillsByCost: null,
    sortState: {
      prop: "productName",
      order: "ascending",
    },
  }),
  mounted() {
    this.fetchFirms();
  },
  computed: {
    SortedData() {
      const sortState = this.sortState;

      return this.SellsGroupedByProductCategory.map(([category, tableData]) => {
        if (!sortState.prop || !sortState.order) return [category, Object.values(tableData)];

        const sorted = Object.values(tableData).sort((a, b) => {
          const valA =
            a?.[sortState.prop]
          const valB =
            b?.[sortState.prop]

          if (valA == null && valB == null) return 0;
          if (valA == null) return 1;
          if (valB == null) return -1;

          if (typeof valA === "number" && typeof valB === "number") {
            return sortState.order === "ascending"
              ? valA - valB
              : valB - valA;
          }
          return sortState.order === "ascending"
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        });

        return [
          category,
          sorted,
        ];
      });

    },
    Shops() {
      return this.Firms.filter(
        (firm) => firm.type === "shop" && !!firm.firm_code
      );
    },
    Firms() {
      return this.firms || [];
    },
    SellsGroupedByProductCategory() {
      const firmCodes = this.firms.reduce(
        (total, firm) => ({ ...total, [firm.firm_code]: firm }),
        {}
      );
      const allProductCodes = this.getAllProductCodes(this.sells, firmCodes);

      return Object.entries(allProductCodes).sort(([, pCode1], [, pCode2]) =>
        pCode1.sortOrder > pCode2.sortOrder ? 1 : -1
      );
    },
    LastTwoYears() {
      const monthFormatter = this.dateMonthFormatter;
      return Array.from({ length: 24 }).map((_, increment) => {
        const date = new Date();
        date.setDate(1);
        date.setMonth(date.getMonth() - increment);
        return {
          name: `${monthFormatter.format(date)} de ${date.getFullYear()}`,
          date,
        };
      });
    },
  },
  methods: {
    handleSortChange({ prop, order }) {
      this.sortState = { prop, order };
    },
    getAllProductCodes(sells, firmCodes) {
      const self = this;

      if (!sells?.length) return {};
      return sells.reduce((total, sell) => {
        const groupKey = self.getCategory(sell);
        if (!total[groupKey])
          total[groupKey] = { sortOrder: self.getCategoryOrder(groupKey) };

        if (!total[groupKey][sell.code])
          total[groupKey][sell.code] = {
            productName: sell.product_name,
            total: Number(sell.total)
          };
        else
          total[groupKey][sell.code].total += Number(sell.total);

        total[groupKey][sell.code][sell.firm_code] = {
          firm: firmCodes?.[sell.firm_code],
          total: Number(sell.total),
          quantity: Number(sell.quantity),
        };
        return total;
      }, {});
    },
    getCategory(sell) {
      if (sell.product_name.includes("BOV")) return "BOVINO";
      if (sell.product_name.includes("FRANG")) return "AVES";
      if (sell.product_name.includes("SUI")) return "SUINOS";
      if (sell.product_name.includes("KIT")) return "KIT";

      if (PRODUCT_CATEGORIES[sell.category_group]?.[sell.category_subgroup])
        return PRODUCT_CATEGORIES[sell.category_group]?.[
          sell.category_subgroup
        ];

      return "OUTROS";
    },
    getCategoryOrder(category) {
      const categoryOrder = {
        BOVINO: 1,
        AVES: 2,
        SUINOS: 3,
        KIT: 4,
        ...Object.values(PRODUCT_CATEGORIES).reduce((total, cat) => {
          return {
            ...total,
            ...Object.values(cat).reduce((tt, cc) => ({ [cc]: 5 }), {}),
          };
        }, {}),
        OUTROS: 6,
      };

      return categoryOrder[category] || 7;
    },
    async fetchFirms() {
      const { firms } = await FirmService().index({ type: "shop" });
      if (firms) this.firms = firms;
    },
    changeDownloadButtonStatus() {
      this.isLoadingDownloadButton = !this.isLoadingDownloadButton;
    },
    groupSellsByProductCategory(sells) {
      if (!sells) {
        return {};
      }

      return Object.values(sells).reduce((total, sell) => {
        const firstSell = sell[0];
        const key =
          firstSell?.product?.at(0)?.category?.name || "SEM CATEGORIA";
        if (total[key]) {
          total[key].push(sell);
        } else {
          total[key] = [sell];
        }

        return total;
      }, {});
    },
    getIds(sells){
      if(!sells?.length) return uuid.v4();

      return sells.map(sell => sell.productName)
    },
    downloadPDF() {
      const monthlySells = this.SortedData;

      const shopFirmCodes = this.Shops.map((shop) => String(shop.firm_code));
      const shopNames = this.Shops.map((shop) => shop.name);

      const headers = [
        [
          "PRODUTO",
          ...shopNames,
          "TOTAL QUANTIDADE",
          "TOTAL VALOR",
          "VALOR MÉDIO",
        ],
      ];

      const content = monthlySells.map(([, products]) => {
        const productRows = products
          .filter((entry) => typeof entry === "object" && entry.productName)
          .map((product) => {
            const row = [product.productName];

            let totalQuantity = 0;
            let totalAmount = 0;

            shopFirmCodes.forEach((firmCode) => {
              const shopData = product[firmCode];
              if (shopData) {
                const quantity = Number(shopData.quantity);
                const amount = Number(shopData.total);
                const avg = quantity > 0 ? amount / quantity : 0;
                totalQuantity += quantity;
                totalAmount += amount;
                row.push(
                  `${numberFormatter.format(
                    quantity
                  )} (${currencyFormatter.format(avg)})`
                );
              } else {
                row.push("-");
              }
            });

            const avgPrice =
              totalQuantity > 0 ? totalAmount / totalQuantity : 0;

            row.push(
              numberFormatter.format(totalQuantity),
              currencyFormatter.format(totalAmount),
              currencyFormatter.format(avgPrice)
            );

            return row;
          });

        // Sort by total amount descending
        productRows.sort((a, b) => {
          const aTotal = parseFloat(
            a[shopNames.length + 2].replace(/[^\d,-]/g, "").replace(",", ".")
          );
          const bTotal = parseFloat(
            b[shopNames.length + 2].replace(/[^\d,-]/g, "").replace(",", ".")
          );
          return bTotal - aTotal;
        });

        return productRows;
      });

      const headerMessage = "Relatório de vendas dos açougues";
      const tableMessages = monthlySells.map(([category]) => category);

      generatePDF({
        fileName: "Relatório de vendas dos açougues",
        headers,
        content,
        headerMessage,
        isMultipleTables: true,
        addTableTitle: true,
        tableMessages,
      });
    },
    async fetchAggregatedSells() {
      const d = new Date(this.selectedMonth);
      const firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const shops = this.Shops;
      const { aggregation } = await SellService().aggregate({
        start_at: firstDay.toISOString(),
        end_at: lastDay.toISOString(),
        firm_ids: shops.map((shop) => shop.uid),
      });
      this.sells = aggregation;
      this.isLoaded = true;
    },
    hasValidNumber(e) {
      return !isNaN(Number(e));
    },
  },
  watch: {
    async selectedMonth() {
      this.fetchAggregatedSells();
    },
  },
};
const getFirstDayInMonth = () => {
  const _ = new Date();
  return new Date(_.getFullYear(), _.getMonth(), 1);
};
</script>
<style scoped>
.el-card {
  width: 100%;
}

tr.unavailable {
  background-color: #fffde7;
}

.is-negative {
  color: red !important;
}

.is-positive {
  color: blue !important;
}

tr.problem {
  background-color: #fbe9e7;
}

.mb-0 {
  margin-bottom: 0px !important;
}

.summary {
  color: #909399;
  font-weight: 700 !important;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", SimSun, sans-serif;
  font-size: 12px;
}

.mt-1 {
  margin-top: 10px !important;
}

.my-2 {
  margin-bottom: 7px !important;
}

.summary-black {
  color: #333 !important;
}

.flex-max {
  flex-basis: max-content !important;
}

.flex-1-1 {
  flex: 1 1 auto;
}

.flex-fit {
  flex-basis: content;
}

.my-auto {
  margin-top: auto !important;
  margin-bottom: auto !important;
}

.pb-1 {
  padding-bottom: 5px;
}

.text-start {
  text-align: start;
}
</style>
